import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { PIN_LENGTH, isWeakPin } from "@shared/pin";
import { api, ApiError, errorMessage } from "@/lib/api";
import { cn } from "@/lib/cn";
import { endpoints } from "@/lib/endpoints";
import { queryClient } from "@/lib/queryClient";
import { queryKeys } from "@/lib/queryKeys";
import { Sheet } from "@/components/overlays";
import { Countdown, Switch } from "@/components/ui";
import { AltArrowLeftIcon, CheckCircleIcon, LinkIcon, LockKeyholeMinimalisticIcon } from "@/components/icons";
import { Avatar } from "../ui";
import { AUTO_SUBMIT_MS, PinDots, PinPad } from "./PinPad";

/*
 * Staff sign in with their phone number and a 4-digit PIN. Nothing here
 * names the PMS, the desk or the property, and nothing links to the desk.
 * Next never checks whether a number exists: it always goes on to the PIN.
 */

type Device = { firstName: string; initials: string };

const fmtNational = (d: string) => (d.length > 5 ? `${d.slice(0, 5)} ${d.slice(5)}` : d);

function Frame({ onBack, children, foot }: { onBack?: () => void; children: ReactNode; foot?: ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col bg-canvas text-ink">
      <div className="flex h-[60px] shrink-0 items-center justify-between px-2">
        {onBack ? (
          <button type="button" onClick={onBack} aria-label="Back" className="flex size-11 items-center justify-center rounded-full active:bg-sunk">
            <AltArrowLeftIcon size={24} />
          </button>
        ) : (
          <span />
        )}
        <span className="pr-3 font-display text-[17px] text-muted" lang="hi">
          काठकुनी
        </span>
      </div>
      <div className="mx-auto flex w-full max-w-[420px] flex-1 flex-col px-6 pb-[calc(24px+env(safe-area-inset-bottom))]">
        <div className="flex flex-1 flex-col">{children}</div>
        {foot}
      </div>
    </div>
  );
}

const H1 = ({ children }: { children: ReactNode }) => <h1 className="mt-4 font-display text-[30px] leading-tight font-semibold">{children}</h1>;
const Sub = ({ children }: { children: ReactNode }) => <p className="mt-2 text-[16px] text-muted">{children}</p>;

function PrimaryButton({ children, onClick, disabled }: { children: ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} className="kadil min-h-[54px] w-full rounded-md bg-deodar px-4 text-[16px] font-bold text-on-fill active:brightness-95 disabled:opacity-[.42]">
      {children}
    </button>
  );
}

function TextLink({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="mx-auto mt-3 flex min-h-11 items-center px-3 text-[15px] font-semibold text-deodar">
      {children}
    </button>
  );
}

export function HelpSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Sheet open={open} onClose={onClose} label="Ask your manager">
      <div className="flex flex-col gap-4 px-6 pt-2 pb-[calc(20px+env(safe-area-inset-bottom))]">
        <h2 className="font-display text-[22px] font-semibold">Ask your manager</h2>
        <ol className="flex flex-col gap-3">
          {[
            <>
              They tap <b>Reset PIN</b> next to your name.
            </>,
            <>You get a link on WhatsApp.</>,
            <>Open it and set a new PIN.</>,
          ].map((t, i) => (
            <li key={i} className="flex items-start gap-3 text-[16px]">
              <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-deodar-tint font-mono text-[13px] text-deodar">{i + 1}</span>
              <span className="pt-0.5">{t}</span>
            </li>
          ))}
        </ol>
        <PrimaryButton onClick={onClose}>OK</PrimaryButton>
      </div>
    </Sheet>
  );
}

function Locked({ until, onBack }: { until: string; onBack: () => void }) {
  return (
    <Frame foot={<PrimaryButton onClick={onBack}>Back to sign in</PrimaryButton>}>
      <span className="mt-10 flex size-16 items-center justify-center rounded-lg text-rhodo" style={{ background: "color-mix(in srgb, var(--rhododendron) 14%, var(--surface))" }}>
        <LockKeyholeMinimalisticIcon size={32} />
      </span>
      <H1>Too many tries</H1>
      <Sub>
        Try again in <Countdown until={until} className="font-semibold text-ink" />, or ask your manager to reset your PIN.
      </Sub>
    </Frame>
  );
}

/** Wrong PIN: the message and how many tries are left, from the server's answer. */
function wrongText(e: unknown): string {
  if (e instanceof ApiError && e.status === 401) {
    const left = (e.details as { triesLeft?: number } | undefined)?.triesLeft;
    return left !== undefined ? `Wrong PIN. ${left} ${left === 1 ? "try" : "tries"} left.` : "That phone number and PIN don't match.";
  }
  return errorMessage(e);
}

function lockedUntil(e: unknown): string | null {
  return e instanceof ApiError && e.status === 423 ? ((e.details as { lockedUntil?: string } | undefined)?.lockedUntil ?? null) : null;
}

export function StaffSignInPage() {
  const navigate = useNavigate();
  const { next } = useSearch({ from: "/staff/sign-in" });
  const device = useQuery({
    queryKey: queryKeys.staff.device,
    queryFn: () => api<Device | null>(endpoints.staffAuth.device, { quiet401: true }),
    staleTime: Infinity,
    retry: false,
  });
  const [step, setStep] = useState<"phone" | "pin">("phone");
  const [digits, setDigits] = useState("");
  const [remember, setRemember] = useState(true);
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [locked, setLocked] = useState<string | null>(null);
  const [help, setHelp] = useState(false);
  const [busy, setBusy] = useState(false);
  const [forgotten, setForgotten] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    document.title = "Sign in";
    return () => clearTimeout(timer.current);
  }, []);

  const remembered = !forgotten && device.data ? device.data : null;

  const submit = async (value: string) => {
    setBusy(true);
    try {
      await api(endpoints.staffAuth.signIn, { method: "POST", body: remembered ? { pin: value, remember } : { phone: digits, pin: value, remember }, quiet401: true });
      queryClient.removeQueries({ queryKey: queryKeys.staff.me });
      void navigate({ to: next && next.startsWith("/staff") ? next : "/staff", replace: true });
    } catch (e) {
      const until = lockedUntil(e);
      if (until) setLocked(until);
      else setError(wrongText(e));
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  const onPin = (v: string) => {
    setError(null);
    setPin(v);
    clearTimeout(timer.current);
    if (v.length === PIN_LENGTH) timer.current = setTimeout(() => void submit(v), AUTO_SUBMIT_MS);
  };

  const notMe = async () => {
    await api(endpoints.staffAuth.device, { method: "DELETE", quiet401: true }).catch(() => null);
    queryClient.setQueryData(queryKeys.staff.device, null);
    setForgotten(true);
    setStep("phone");
    setPin("");
    setError(null);
  };

  if (locked) return <Locked until={locked} onBack={() => (setLocked(null), setStep(remembered ? "pin" : "phone"), setPin(""))} />;
  if (device.isPending) return <Frame>{null}</Frame>;

  if (remembered) {
    return (
      <Frame foot={<TextLink onClick={() => void notMe()}>Not {remembered.firstName}?</TextLink>}>
        <div className="mt-6 flex flex-col items-center text-center">
          <Avatar initials={remembered.initials} size={64} />
          <H1>Hi {remembered.firstName}</H1>
          <Sub>Enter your PIN</Sub>
        </div>
        <div className="mt-8 flex flex-col gap-3">
          <PinDots length={pin.length} error={!!error} />
          <p className={cn("min-h-6 text-center text-[15px] font-semibold text-rhodo", !error && "invisible")}>{error ?? "·"}</p>
          <PinPad value={pin} onChange={onPin} disabled={busy} />
          <TextLink onClick={() => setHelp(true)}>Forgot PIN?</TextLink>
        </div>
        <HelpSheet open={help} onClose={() => setHelp(false)} />
      </Frame>
    );
  }

  if (step === "pin") {
    return (
      <Frame onBack={() => (setStep("phone"), setPin(""), setError(null))}>
        <H1>Enter your PIN</H1>
        <Sub>For +91 {fmtNational(digits)}</Sub>
        <div className="mt-8 flex flex-col gap-3">
          <PinDots length={pin.length} error={!!error} />
          <p className={cn("min-h-6 text-center text-[15px] font-semibold text-rhodo", !error && "invisible")}>{error ?? "·"}</p>
          <PinPad value={pin} onChange={onPin} disabled={busy} />
          <TextLink onClick={() => setHelp(true)}>Forgot PIN?</TextLink>
        </div>
        <HelpSheet open={help} onClose={() => setHelp(false)} />
      </Frame>
    );
  }

  return (
    <Frame
      foot={
        <>
          <PrimaryButton disabled={digits.length !== 10} onClick={() => setStep("pin")}>
            Next
          </PrimaryButton>
          <TextLink onClick={() => setHelp(true)}>Can&rsquo;t sign in?</TextLink>
        </>
      }
    >
      <H1>Sign in</H1>
      <Sub>Use the phone number your manager added.</Sub>
      <label className="mt-8 flex h-[60px] items-center rounded-md border border-line-strong bg-surface focus-within:border-deodar focus-within:shadow-[0_0_0_3px_var(--deodar-tint)]">
        <span className="flex h-full items-center border-r border-line px-4 font-mono text-[18px] text-muted">+91</span>
        <input
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          aria-label="Phone number"
          placeholder="98160 22314"
          value={fmtNational(digits)}
          onChange={(e) => setDigits(e.target.value.replace(/\D/g, "").replace(/^91(?=\d{10}$)/, "").slice(0, 10))}
          onKeyDown={(e) => e.key === "Enter" && digits.length === 10 && setStep("pin")}
          className="h-full min-w-0 flex-1 bg-transparent px-4 font-mono text-[20px] outline-none placeholder:text-subtle"
        />
      </label>
      <div className="mt-5">
        <Switch checked={remember} onChange={setRemember} label="Keep me signed in" hint="On this phone, for 30 days" />
      </div>
      <HelpSheet open={help} onClose={() => setHelp(false)} />
    </Frame>
  );
}

/* ───────────────────────────── First time / reset ───────────────────────────── */

export function StaffJoinPage() {
  const { token } = useParams({ from: "/staff/join/$token" });
  const navigate = useNavigate();
  const info = useQuery({
    queryKey: queryKeys.staff.join(token),
    queryFn: () => api<{ firstName: string; purpose: "invite" | "reset" }>(endpoints.staffAuth.join(token), { quiet401: true }),
    retry: false,
    staleTime: Infinity,
  });
  const [first, setFirst] = useState<string | null>(null);
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    document.title = "Set your PIN";
    return () => clearTimeout(timer.current);
  }, []);

  const finish = async (chosen: string) => {
    setBusy(true);
    try {
      await api(endpoints.staffAuth.join(token), { method: "POST", body: { pin: chosen }, quiet401: true });
      setDone(true);
      // A fresh sign-in: drop what an earlier session left. Not the link's own
      // query, which would now (rightly) say the link is spent.
      queryClient.removeQueries({ queryKey: queryKeys.staff.me });
      queryClient.removeQueries({ queryKey: queryKeys.staff.device });
    } catch (e) {
      if (e instanceof ApiError && e.status === 410) void info.refetch();
      setFirst(null);
      setPin("");
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onPin = (v: string) => {
    setError(null);
    setPin(v);
    clearTimeout(timer.current);
    if (v.length !== PIN_LENGTH) return;
    timer.current = setTimeout(() => {
      if (first === null) {
        if (isWeakPin(v)) {
          setError("Too easy to guess. Try another.");
          setPin("");
          return;
        }
        setFirst(v);
        setPin("");
        return;
      }
      if (v !== first) {
        setFirst(null);
        setPin("");
        setError("Those didn't match. Start again.");
        return;
      }
      void finish(v);
    }, AUTO_SUBMIT_MS);
  };

  if (done) {
    return (
      <Frame foot={<PrimaryButton onClick={() => void navigate({ to: "/staff", replace: true })}>Open my work</PrimaryButton>}>
        <span className="mt-10 flex size-16 items-center justify-center rounded-lg text-pine" style={{ background: "color-mix(in srgb, var(--pine) 14%, var(--surface))" }}>
          <CheckCircleIcon size={34} />
        </span>
        <H1>You&rsquo;re all set</H1>
        <Sub>Use your phone number and this PIN every time you sign in.</Sub>
      </Frame>
    );
  }
  if (info.isPending) return <Frame>{null}</Frame>;
  if (info.isError) {
    return (
      <Frame>
        <span className="mt-10 flex size-16 items-center justify-center rounded-lg bg-sunk text-muted">
          <LinkIcon size={32} />
        </span>
        <H1>This link has expired</H1>
        <Sub>Links work for 48 hours, once. Ask your manager to send a new one.</Sub>
      </Frame>
    );
  }
  return (
    <Frame onBack={first !== null ? () => (setFirst(null), setPin(""), setError(null)) : undefined}>
      {first === null ? (
        <>
          <H1>Welcome, {info.data.firstName}</H1>
          <Sub>Choose a 4-digit PIN. You will use it every time you sign in.</Sub>
        </>
      ) : (
        <>
          <H1>Enter it again</H1>
          <Sub>Just to be sure.</Sub>
        </>
      )}
      <div className="mt-8 flex flex-col gap-3">
        <PinDots length={pin.length} error={!!error} />
        <p className={cn("min-h-6 text-center text-[15px] font-semibold text-rhodo", !error && "invisible")}>{error ?? "·"}</p>
        <PinPad value={pin} onChange={onPin} disabled={busy} />
      </div>
    </Frame>
  );
}
